/**
 * Voorraad / Magazijn — types, labels, badges, DTOs en Zod-schemas.
 *
 * Spec: docs/specs/voorraad-magazijn-module.md §4.
 *
 * Architectuur:
 *   - Mutaties zijn append-only (`VoorraadMutatie`) en lopen ALTIJD via
 *     `boekMutatie()` in apps/api/src/services/voorraad-service.ts.
 *   - Saldo (`VoorraadSaldo`) is een cache van SUM(mutaties) per (artikel,
 *     locatie) en wordt door dezelfde service in-transactie geüpdatet.
 *   - Negatief saldo is altijd een fout — service gooit
 *     "Onvoldoende voorraad (huidig saldo: …)" en rolt de transactie terug.
 */
import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { idParam } from './id-validators'
import type { VoorraadUniekStatus } from './voorraad-uniek'
import type { MiddelMutatieBron } from './middelen'
import { partialZonderDefaults } from './zod-patch'
// batch-69 magazijn-cijfers (FO-520) — alleen een type (geen runtime-cirkel).
import type { VoorraadLaatstGeteldDTO } from './voorraad-telling'

// ─── Enums ───────────────────────────────────────────────────────────────────

export const VOORRAAD_CATEGORIE = [
  'WERKKLEDING',
  'PBM',
  'GEREEDSCHAP',
  'ELEKTRONICA',
  'KOFFERS',
  'KANTOORARTIKELEN',
  'VERBRUIKSMATERIAAL',
  // Spec 30 — traceerbare (unieke) categorieën (fase 1: enum-uitbreiding)
  'TELEFOON',
  'SIMKAART',
  'LAPTOP',
  'TABLET',
  'AUTOSLEUTEL',
  'VOERTUIGMIDDEL',
  'OVERIG',
] as const
export type VoorraadCategorie = (typeof VOORRAAD_CATEGORIE)[number]

export const VOORRAAD_CATEGORIE_LABELS: Record<VoorraadCategorie, string> = {
  WERKKLEDING:        'Werkkleding',
  PBM:                'PBM',
  GEREEDSCHAP:        'Gereedschap',
  ELEKTRONICA:        'Elektronica',
  KOFFERS:            'Koffers & cases',
  KANTOORARTIKELEN:   'Kantoorartikelen',
  VERBRUIKSMATERIAAL: 'Verbruiksmateriaal',
  TELEFOON:           'Telefoon',
  SIMKAART:           'Simkaart',
  LAPTOP:             'Laptop',
  TABLET:             'Tablet',
  AUTOSLEUTEL:        'Autosleutel',
  VOERTUIGMIDDEL:     'Voertuigmiddel',
  OVERIG:             'Overig',
}

// ─── FO-153 (batch-30) — hoofdgroepen (afgeleide soort-as) ──────────────────
//
// De 14 `VoorraadCategorie`-waarden mengen twee assen (soort én trackingmodel).
// De hoofdgroep is de vaste soort-as voor de gescheiden voorraad-weergave:
// ALTIJD afgeleid uit de categorie via deze mapping, nooit apart opgeslagen
// (één waarheid, geen schemawijziging). Elke categorie valt in precies één
// hoofdgroep; een onbekende (nieuwe) categorie valt met een dev-waarschuwing
// terug op OVERIG — voeg 'm bij een enum-uitbreiding hier direct toe (zie de
// "nieuwe categorie"-checklist in docs/modules/materieel.md).
//
// Let op: hoofdgroep APPARATUUR is bewust NIET gelijk aan APPARATUUR_CATEGORIEEN
// (voorraad-uniek.ts): die laatste is het filter van de Materieel > Apparatuur-
// tab en bevat ook OVERIG als vangnet; AUTOSLEUTEL/VOERTUIGMIDDEL blijven aan
// voertuigen hangen en vallen hier onder OVERIG.

export const VOORRAAD_HOOFDGROEP = [
  'KLEDING',
  'PBM',
  'GEREEDSCHAP',
  'APPARATUUR',
  'VERBRUIK',
  'KANTOOR',
  'OVERIG',
] as const
export type VoorraadHoofdgroep = (typeof VOORRAAD_HOOFDGROEP)[number]

export const VOORRAAD_HOOFDGROEP_LABELS: Record<VoorraadHoofdgroep, string> = {
  KLEDING:     'Kleding',
  PBM:         'PBM',
  GEREEDSCHAP: 'Gereedschap',
  APPARATUUR:  'Apparatuur',
  VERBRUIK:    'Verbruik',
  KANTOOR:     'Kantoor',
  OVERIG:      'Overig',
}

export const VOORRAAD_CATEGORIE_HOOFDGROEP: Record<VoorraadCategorie, VoorraadHoofdgroep> = {
  WERKKLEDING:        'KLEDING',
  PBM:                'PBM',
  GEREEDSCHAP:        'GEREEDSCHAP',
  KOFFERS:            'GEREEDSCHAP', // gereedschapskoffers/systeemkoffers horen bij het gereedschap
  ELEKTRONICA:        'APPARATUUR',
  TELEFOON:           'APPARATUUR',
  SIMKAART:           'APPARATUUR',
  LAPTOP:             'APPARATUUR',
  TABLET:             'APPARATUUR',
  KANTOORARTIKELEN:   'KANTOOR',
  VERBRUIKSMATERIAAL: 'VERBRUIK',
  AUTOSLEUTEL:        'OVERIG', // voertuig-gebonden, geen apparatuur (zie APPARATUUR_CATEGORIEEN-comment)
  VOERTUIGMIDDEL:     'OVERIG',
  OVERIG:             'OVERIG',
}

/**
 * Hoofdgroep van een categorie. Accepteert een ruwe string (DB-waarde) zodat
 * een nog-niet-gemapte nieuwe enum-waarde niet crasht maar met een
 * dev-waarschuwing in OVERIG valt.
 */
export function voorraadHoofdgroep(categorie: string): VoorraadHoofdgroep {
  const hoofdgroep = VOORRAAD_CATEGORIE_HOOFDGROEP[categorie as VoorraadCategorie]
  if (!hoofdgroep) {
    if (typeof console !== 'undefined') {
      console.warn(
        `[voorraad] Onbekende VoorraadCategorie "${categorie}" valt terug op hoofdgroep OVERIG - voeg de mapping toe in VOORRAAD_CATEGORIE_HOOFDGROEP (packages/types/src/voorraad.ts).`,
      )
    }
    return 'OVERIG'
  }
  return hoofdgroep
}

/** Alle categorieën die in een hoofdgroep vallen (voor filters/queries). */
export function categorieenVoorHoofdgroep(hoofdgroep: VoorraadHoofdgroep): VoorraadCategorie[] {
  return VOORRAAD_CATEGORIE.filter((c) => VOORRAAD_CATEGORIE_HOOFDGROEP[c] === hoofdgroep)
}

export const VOORRAAD_EENHEID = [
  'STUK',
  'PAAR',
  'SET',
  'DOOS',
  'ROL',
  'METER',
  'LITER',
  'KG',
] as const
export type VoorraadEenheid = (typeof VOORRAAD_EENHEID)[number]

export const VOORRAAD_EENHEID_LABELS: Record<VoorraadEenheid, string> = {
  STUK:  'stuk',
  PAAR:  'paar',
  SET:   'set',
  DOOS:  'doos',
  ROL:   'rol',
  METER: 'meter',
  LITER: 'liter',
  KG:    'kg',
}

export const VOORRAAD_MUTATIE_TYPE = [
  'INKOOP',
  'UITGIFTE',
  'RETOUR',
  'AFBOEKING',
  'CORRECTIE_PLUS',
  'CORRECTIE_MIN',
  'VERPLAATSING_IN',
  'VERPLAATSING_UIT',
  'WERKBON',
] as const
export type VoorraadMutatieType = (typeof VOORRAAD_MUTATIE_TYPE)[number]

export const VOORRAAD_MUTATIE_LABELS: Record<VoorraadMutatieType, string> = {
  INKOOP:           'Inkoop',
  UITGIFTE:         'Uitgifte',
  RETOUR:           'Retour',
  AFBOEKING:        'Afboeking',
  CORRECTIE_PLUS:   'Correctie (+)',
  CORRECTIE_MIN:    'Correctie (−)',
  VERPLAATSING_IN:  'Verplaatsing (in)',
  VERPLAATSING_UIT: 'Verplaatsing (uit)',
  WERKBON:          'Werkbon-verbruik',
}

export type VoorraadMutatieBadgeVariant =
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'secondary'

export const VOORRAAD_MUTATIE_BADGE: Record<VoorraadMutatieType, VoorraadMutatieBadgeVariant> = {
  INKOOP:           'success',
  UITGIFTE:         'warning',
  RETOUR:           'info',
  AFBOEKING:        'danger',
  CORRECTIE_PLUS:   'success',
  CORRECTIE_MIN:    'danger',
  VERPLAATSING_IN:  'info',
  VERPLAATSING_UIT: 'secondary',
  WERKBON:          'warning',
}

// ─── Richtings-helpers ──────────────────────────────────────────────────────

/**
 * Inkomend = saldo erbij (+). Uitgaand = saldo eraf (-).
 * Single source of truth voor de richting per mutatie-type — gebruikt door
 * zowel de service als de UI (bv. "+5" / "-2" weergave).
 */
export const VOORRAAD_INKOMEND_TYPES: readonly VoorraadMutatieType[] = [
  'INKOOP',
  'RETOUR',
  'CORRECTIE_PLUS',
  'VERPLAATSING_IN',
] as const

export function isInkomend(type: VoorraadMutatieType): boolean {
  return VOORRAAD_INKOMEND_TYPES.includes(type)
}

// ─── Minimum-voorraad helpers (spec §4 + §5.2 KPI) ──────────────────────────

/**
 * Bij saldo <= drempel wordt een artikel als "laag in voorraad" gemarkeerd.
 * Default als artikel zelf geen `minimumVoorraad` ingesteld heeft. Kan ook
 * gebruikt worden voor een algemene UI-grens als de organisatie nog geen
 * artikel-specifiek beleid heeft ingesteld.
 */
export const MIN_VOORRAAD_DREMPEL = 0

/**
 * `true` zodra het huidige saldo het minimum bereikt of eronder zakt.
 * Centraal gehouden zodat backend/frontend exact dezelfde grens hanteren.
 *
 * Een minimum van 0 telt NIET als drempel. Zonder die klem is `0 <= 0` waar en
 * geldt elk nooit-bevoorraad artikel als "laag": op prod raakte dat 435 van de
 * 465 bulk-artikelen, waardoor de alerts-pagina en de lijst-badge ruis werden en
 * de 30 artikelen met een écht ingesteld beleid onvindbaar. `minimumVoorraad`
 * blijft daarmee wat de naam belooft: een bewust gekozen ondergrens.
 * `inkoop-besteltraject` filterde hier al los op `minimumVoorraad > 0`; die
 * regel staat nu op één plek.
 */
export function isLaagInVoorraad(totaalSaldo: number, minimumVoorraad: number): boolean {
  if (minimumVoorraad <= 0) return false
  return totaalSaldo <= minimumVoorraad
}

/**
 * Wat er nodig is om een artikel weer op peil te brengen.
 *
 * Eén bron voor de drie plekken die hetzelfde rekensommetje tonen: de tekst van
 * het VOORRAAD_LAAG-signaal, de `ONDER_MINIMUM`-alerts en de voorraad-/
 * uitrustingschermen. Puur, zodat backend en UI nooit verschillende aantallen
 * kunnen adviseren.
 *
 * `maximumVoorraad` (batch-69, FO-521: het aanvulniveau/plafond, voorheen
 * "gewenste voorraad") mag `null` zijn (niet ingesteld); in dat geval valt het
 * aanvuladvies terug op het minimum. Een maximum ónder het minimum kan sinds
 * batch-69 niet meer worden opgeslagen (schema + CHECK-constraint); de klem
 * blijft staan zodat het advies nooit onder de ondergrens uitkomt.
 */
export interface AanvulAdvies {
  /** Saldo ligt op of onder een ingestelde (> 0) minimumvoorraad. */
  onderMinimum: boolean
  /** Stuks tot aan het minimum (0 als het saldo er al op of boven ligt). */
  tekortTotMinimum: number
  /**
   * Stuks tot aan het maximum. Zonder ingesteld (of te laag) maximum gelijk aan
   * `tekortTotMinimum`.
   */
  tekortTotMaximum: number
  /** Het niveau waar `tekortTotMaximum` naartoe rekent (maximum, anders minimum). */
  aanvullenTot: number
  /** True wanneer er een expliciet maximum op of boven het minimum staat. */
  heeftMaximum: boolean
}

export function berekenAanvulAdvies(input: {
  totaalSaldo: number
  minimumVoorraad: number
  maximumVoorraad: number | null | undefined
}): AanvulAdvies {
  const { totaalSaldo, minimumVoorraad } = input
  const maximum = input.maximumVoorraad ?? null
  // `>=` en niet `>`: een maximum dat gelijk is aan het minimum is een bewuste
  // keuze van de gebruiker en moet in de UI ook zo heten ("tot het maximum"),
  // ook al is het aantal identiek. Alleen een waarde ónder het minimum wordt
  // genegeerd - het advies mag nooit onder de ondergrens uit.
  const heeftMaximum = maximum !== null && maximum >= minimumVoorraad
  const aanvullenTot = heeftMaximum ? maximum : minimumVoorraad
  return {
    onderMinimum: isLaagInVoorraad(totaalSaldo, minimumVoorraad),
    tekortTotMinimum: Math.max(0, minimumVoorraad - totaalSaldo),
    tekortTotMaximum: Math.max(0, aanvullenTot - totaalSaldo),
    aanvullenTot,
    heeftMaximum,
  }
}

// ─── FO-151 (batch-30) — vaklocatie Zod-schema's (gedeeld API + form) ────────
//
// Opslag is gestructureerd: drie nullable ints. Hiërarchie (vak vereist plank,
// plank vereist stelling) wordt hier afgedwongen voor complete payloads; bij
// partial updates (uniek-item-PATCH) valideert de service de samengevoegde
// staat met `isGeldigeVaklocatie` uit @streep/utils (types kan niet op utils
// steunen — omgekeerde dependency-richting).

export const vaklocatieVeldenSchema = z.object({
  /** Stellingnummer (1-999); null = geen vaklocatie. */
  stelling: z.number().int().min(1).max(999).nullable(),
  /** Planknummer (0-999; 0 = onderste bord/grijpvoorraad). */
  plank: z.number().int().min(0).max(999).nullable(),
  /** Vaknummer (0-999). */
  vak: z.number().int().min(0).max(999).nullable(),
})
export type VaklocatieVelden = z.infer<typeof vaklocatieVeldenSchema>

/**
 * Zod-refine voor de lijst-/exportfilters: de vaklocatie-hiërarchie plus
 * "Zonder vaklocatie" sluit een stelling uit (het is óf het een óf het ander).
 */
export function vaklocatieFilterRefine(
  val: {
    stelling?: number | null
    plank?: number | null
    vak?: number | null
    zonderVaklocatie?: boolean
  },
  ctx: z.RefinementCtx,
): void {
  vaklocatieHierarchieRefine(val, ctx)
  if (val.zonderVaklocatie && val.stelling != null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Kies een stelling of "Zonder vaklocatie", niet allebei',
      path: ['zonderVaklocatie'],
    })
  }
}

/** Zod-refine voor de hiërarchie: vak vereist plank, plank vereist stelling. */
export function vaklocatieHierarchieRefine(
  val: { stelling?: number | null; plank?: number | null; vak?: number | null },
  ctx: z.RefinementCtx,
): void {
  if (val.plank != null && val.stelling == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Een plank vereist een stelling',
      path: ['plank'],
    })
  }
  if (val.vak != null && val.plank == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Een vak vereist een plank',
      path: ['vak'],
    })
  }
}

/**
 * PATCH /voorraad/artikelen/:id/vaklocatie — zet of wis (alles null) de
 * vaklocatie van een artikel op één magazijnlocatie.
 */
export const setVaklocatieSchema = vaklocatieVeldenSchema
  .extend({ locatieId: z.string().min(1) })
  .superRefine(vaklocatieHierarchieRefine)
export type SetVaklocatieInput = z.infer<typeof setVaklocatieSchema>

/** Eén rij in de bulk-toewijzing (POST /voorraad/locaties/:id/vaklocaties). */
export const bulkVaklocatieRijSchema = vaklocatieVeldenSchema
  .extend({ artikelId: z.string().min(1) })
  .superRefine(vaklocatieHierarchieRefine)
export type BulkVaklocatieRij = z.infer<typeof bulkVaklocatieRijSchema>

export const bulkVaklocatieSchema = z.object({
  rijen: z.array(bulkVaklocatieRijSchema).min(1).max(200),
})
export type BulkVaklocatieInput = z.infer<typeof bulkVaklocatieSchema>

// ─── DTOs ───────────────────────────────────────────────────────────────────

export interface VoorraadArtikelListRowDTO {
  id: string
  artikelNummer: string
  naam: string
  categorie: VoorraadCategorie
  eenheid: VoorraadEenheid
  maat: string | null
  kleur: string | null
  inkoopPrijs: number | null
  totaalSaldo: number      // SUM over alle locaties; voor traceerbaar = telling OP_VOORRAAD-items
  minimumVoorraad: number
  /** batch-69 (FO-521) — aanvulniveau/plafond; null = niet ingesteld. */
  maximumVoorraad: number | null
  isLaag: boolean          // totaalSaldo <= minimumVoorraad (alleen bij minimum > 0)
  locaties: number         // aantal locaties met saldo > 0
  /**
   * Artikelfoto. Twee vormen (zelfde contract als `UitrustingArtikelDTO.fotoPath`):
   * een blob-storage-key (geüploade foto → stream via de same-origin
   * `/api/v1/voorraad/artikelen/:id/foto`-proxy) OF een absolute http(s)-URL
   * (extern, bv. een leverancier-/shopping-afbeelding → direct als `<img src>`).
   * Gebruik `voorraadArtikelFotoSrc()` om de juiste `src` te kiezen.
   */
  fotoUrl: string | null
  /** true = individueel traceerbaar (VoorraadUniekItem); false = bulk (VoorraadSaldo) */
  traceerbaar: boolean
  /** QR-token voor inline QR-modal op de lijst. */
  qrToken: string | null
  /**
   * FO-151 — vaklocatie-code ("3-1-3") van de saldo-rij op de gefilterde
   * locatie; zonder locatie-filter de eerste magazijn-rij mét vaklocatie.
   * Null = geen vaklocatie toegewezen (of geen magazijn-saldo).
   */
  vaklocatie: string | null
  /** FO-151 — saldo op de gefilterde locatie; alleen gevuld bij een locatieId-filter. */
  locatieSaldo: number | null
  /**
   * FO-153 — eerstvolgende keuringsdatum over de niet-afgeschreven exemplaren
   * (ISO-string). Alleen gevuld voor traceerbare artikelen; kolom in de
   * PBM-/Gereedschap-hoofdgroep.
   */
  eerstvolgendeKeuring: string | null
  /** FO-153 — eerstvolgende houdbaarheids-/vervangdatum over de exemplaren (PBM). */
  eersteHoudbaarheid: string | null
  /**
   * Losse voorraad (som van de positieve saldo-rijen) op een TRACEERBAAR artikel.
   * Die telt niet mee in `totaalSaldo` (dat is de exemplaar-telling) en is dus
   * onzichtbaar tot hij via `POST /voorraad/artikelen/:id/los-saldo-naar-exemplaren`
   * is omgezet. Altijd 0 bij een bulk-artikel.
   */
  losSaldo: number
}

/** Resultaat van `POST /voorraad/artikelen/:id/los-saldo-naar-exemplaren`. */
export interface LosSaldoOmzettingResultDTO {
  artikelId: string
  aantalOmgezet: number
  exemplaarIds: string[]
}

/**
 * Herkent een artikelfoto-STORAGE-KEY zoals de upload-endpoints die schrijven:
 * `voorraad/artikelen/<id>/<bestand>` of `uitrusting/artikelen/<id>/<bestand>`.
 * De foto-GET-routes streamen UITSLUITEND keys in deze namespace en het
 * write-schema accepteert náást een http(s)-URL alleen deze vorm. Zonder die
 * klem zou `fotoUrl` een vrij tekstveld zijn dat als blob-key gestreamd wordt,
 * en kan iemand met VOORRAAD.canEdit een pointer naar élke blob in de
 * gedeelde container planten (documenten, loonstroken) die daarna via de
 * portal-toegankelijke foto-GET leesbaar is (broken access control).
 */
export function isArtikelFotoStorageKey(waarde: string): boolean {
  return /^(?:voorraad|uitrusting)\/artikelen\/[A-Za-z0-9_-]+\/[A-Za-z0-9._-]+$/.test(waarde)
}

/**
 * Gedeeld schema voor een foto-bronveld: absolute http(s)-URL (externe
 * leverancier-/shopping-afbeelding) óf een artikelfoto-storage-key (gezet door
 * de upload-endpoints). Bewust GEEN kale `z.string()`: zie
 * {@link isArtikelFotoStorageKey}. En bewust geen `.url()`: het write-schema
 * moet zijn eigen GET-read-DTO blijven accepteren nadat een upload er een
 * storage-key in zette (contract-test-harness).
 */
export const fotoBronSchema = z
  .string()
  .trim()
  .min(1)
  .max(500)
  .refine((v) => /^https?:\/\//i.test(v) || isArtikelFotoStorageKey(v), {
    message: 'Foto moet een http(s)-URL of een geüploade artikelfoto zijn',
  })

/**
 * Korte, stabiele cache-versie van de opgeslagen foto-waarde (djb2 → base36).
 * De proxy-URL (`…/:id/foto`) is stabiel per artikel terwijl de API
 * `Cache-Control: public, max-age=3600` zet; zonder versie-parameter toont
 * "Foto vervangen" dus tot een uur de oude afbeelding uit de browser-cache.
 * De upload-endpoints maken de key per upload uniek, dus waarde-hash = verse
 * URL na elke vervanging.
 */
export function fotoCacheVersie(waarde: string): string {
  let hash = 5381
  for (let i = 0; i < waarde.length; i++) {
    hash = ((hash << 5) + hash + waarde.charCodeAt(i)) | 0
  }
  return (hash >>> 0).toString(36)
}

/**
 * Kies de `<img src>` voor een voorraadartikel-foto (spiegel van
 * `artikelFotoSrc()` in uitrusting.ts — zelfde twee-vormige waarde):
 *   - absolute http(s)-URL → direct renderen (de stream-proxy zou hierop een
 *     placeholder geven, het is geen blob-key);
 *   - blob-storage-key (geüploade foto) → via de same-origin proxy die de
 *     Bearer server-side bijzet, mét `?v=`-cache-versie (zie
 *     {@link fotoCacheVersie}).
 * Geeft `null` terug als er geen foto is (caller toont dan de placeholder).
 * `proxyBase` default `/api/v1/voorraad/artikelen`: backoffice én portal
 * mounten hun proxy op dit pad (spiegel van het API-pad, zoals de
 * medewerkersfoto), dus dezelfde relatieve URL werkt in beide apps.
 */
export function voorraadArtikelFotoSrc(
  artikel: Pick<VoorraadArtikelListRowDTO, 'id' | 'fotoUrl'>,
  proxyBase = '/api/v1/voorraad/artikelen',
): string | null {
  if (!artikel.fotoUrl) return null
  if (/^https?:\/\//i.test(artikel.fotoUrl)) return artikel.fotoUrl
  return `${proxyBase}/${artikel.id}/foto?v=${fotoCacheVersie(artikel.fotoUrl)}`
}

export interface VoorraadSaldoDTO {
  locatieId: string
  locatieNaam: string
  aantal: number
  /** FO-151 — locatietype (MAGAZIJN | SERVICEBUS | KANTOOR | EXTERN); vaklocatie-invoer alleen bij MAGAZIJN. */
  locatieType: string
  /** FO-151 — vaklocatie-velden van deze saldo-rij (null = niet toegewezen). */
  stelling: number | null
  plank: number | null
  vak: number | null
  /** FO-151 — afgeleide code "3-1-3" (formatVaklocatie); null = geen vaklocatie. */
  vaklocatie: string | null
}

export interface VoorraadMutatieDTO {
  id: string
  type: VoorraadMutatieType
  aantal: number
  locatieNaam: string
  medewerkerNaam: string | null
  werkbonNummer: string | null
  notitie: string | null
  createdByNaam: string
  createdAt: string
  /** Alleen gevuld bij de module-brede mutaties-lijst (GET /voorraad/mutaties). */
  artikelNaam?: string
  artikelNummer?: string
  artikelId?: string
  locatieId?: string
  employeeId?: string
  werkbonId?: string
  /**
   * FO-151 — huidige vaklocatie-code van de saldo-rij (artikel x locatie) van
   * deze mutatie; null = geen vaklocatie. Snapshot van NU, niet van het
   * boekmoment (vaklocatie-wijzigingen zijn geen mutaties).
   */
  vaklocatie?: string | null
  /** batch-69 (FO-520 §C15) — correctie uit een telsessie: id + nummer (link). */
  tellingId?: string | null
  tellingNummer?: string | null
}

/**
 * Eén rij in de module-brede uitgiftes-overzicht (GET /voorraad/uitgiftes).
 * Elke rij staat voor ofwel een open bulk-uitgifte (BULK) ofwel een uniek item
 * met status UITGEGEVEN (UNIEK). Beide zijn nog-niet-geretourneerde uitgiftes.
 */
export interface VoorraadUitgifteRowDTO {
  /** 'BULK' = bulk-uitgifte (VoorraadMutatie); 'UNIEK' = uniek item (VoorraadUniekItem). */
  kind: 'BULK' | 'UNIEK'
  /** ID van het artikel. */
  artikelId: string
  artikelNaam: string
  artikelNummer: string
  categorie: VoorraadCategorie
  /** Medewerker aan wie uitgegeven. */
  employeeId: string
  medewerkerNaam: string
  /** Bulk: netto uitgegeven aantal (nog niet teruggegeven). Uniek: 1. */
  aantal: number
  /** Uniek: serienummer / asset-tag / IMEI als leesbare code. Bulk: null. */
  interneCode: string | null
  /** Datum van (eerste) uitgifte. */
  sindsAt: string
  /** Uniek item id (voor innemen-actie). Bulk: null. */
  uniekItemId: string | null
  /**
   * Herkomst-/retourlocatie (default voor innemen/retour): bulk = locatie van
   * de uitgifte-mutatie; uniek = laatst bekende locatie uit het
   * gebeurtenissen-log, met als fallback de locatie waar de meeste
   * OP_VOORRAAD-exemplaren van het artikel liggen. Null = onbekend.
   */
  locatieId: string | null
  /**
   * FO-151 — vaklocatie-code van de saldo-rij op de uitgifte-locatie (bulk);
   * voor uniek-rijen null (het vak is bij uitgifte leeggemaakt).
   */
  vaklocatie: string | null
  /**
   * Kist-inname — voorgestelde kar/koffer-bestemming: dit artikel (of dit
   * exemplaar) staat in die kist als ONTBREEKT/KWIJT open. Exemplaar-match
   * wint van artikel-match. Null = geen kist-suggestie.
   *
   * De suggestie is alleen de DEFAULT-bestemming als de kist niet bij een
   * ándere medewerker ligt dan de rij (houderEmployeeId) — je kunt fysiek
   * niets in een kist leggen die bij een collega in het veld is.
   */
  kistSuggestie: {
    dragerId: string
    /** Asset-tag van het kist-exemplaar (bv. "KOF1001"). */
    dragerNummer: string
    dragerNaam: string
    /** Lade van de open ONTBREEKT/KWIJT-regel. */
    ladeId: string
    ladeNummer: number
    reden: 'ONTBREEKT' | 'KWIJT'
    /** Afgeleide plek van de kist ("bij P. van Dam" / locatienaam / "in bus …"). */
    plekLabel: string
    /** Houder van de kist (employeeId) wanneer die is uitgegeven; anders null. */
    houderEmployeeId: string | null
  } | null
  /** Suggestie-lade van het artikel bij toevoegen aan een drager (FO-310). */
  standaardLade: number | null
}

export interface VoorraadArtikelDetailDTO extends VoorraadArtikelListRowDTO {
  omschrijving: string | null
  leverancier: string | null
  /** FO-059 — gestructureerde leverancier (Client type=LEVERANCIER) + naam. */
  leverancierId: string | null
  leverancierNaam: string | null
  leverancierArtNr: string | null
  verkoopPrijs: number | null
  bestelDrempel: number | null
  bestelAantal: number | null
  actief: boolean
  saldi: VoorraadSaldoDTO[]
  recenteMutaties: VoorraadMutatieDTO[]
  createdAt: string
  /** Beschikbaar voor bestelling via bestelmodule (keuze 1 spec 30). */
  bestelbaar: boolean
  /** Interne creditkosten per eenheid (spec 30, keuze 1). */
  creditKosten: number | null
  /** QR-token voor het artikel zelf (bulk); crypto-random. */
  qrToken: string | null
  /** Vaste inhoud van een set-artikel (leeg = gewoon artikel). */
  vasteInhoud: VasteInhoudRegelDTO[]
  /** batch-69 (FO-521 §C10) — nooit automatisch bestellen (altijd via de goedkeuringenlijst). */
  autoBestellenUitgesloten: boolean
  /** batch-69 (FO-521 §A5) — wanneer en via welke bron de inkoopprijs het laatst wijzigde. */
  inkoopPrijsGewijzigdOp: string | null
  inkoopPrijsBron: VoorraadPrijsBron | null
  /**
   * batch-69 (FO-521 §A5) — open prijsvoorstel na een levering met een afwijkende
   * prijs (null = geen). Zichtbaar voor wie het artikel mag zien, net als de
   * inkoopprijs op dit detail.
   */
  prijsVoorstel: VoorraadPrijsVoorstelDTO | null
  /** batch-69 (FO-520 §C15) — de laatste afgeronde telling waarin dit artikel geteld is. */
  laatstGeteld: VoorraadLaatstGeteldDTO | null
}

// ── batch-69 magazijn-cijfers (FO-521 §A5) — prijs-audit ──────────────────
// Elke wijziging van een inkoopprijs (artikel) of aanschafbedrag (uniek item)
// schrijft een VoorraadArtikelBeheerAudit-regel PRIJS_GEWIJZIGD met oud/nieuw,
// actor en bron. Een levering met een afwijkende prijs overschrijft NIETS: hij
// zet een voorstel klaar dat met één klik geaccepteerd of afgewezen wordt.

/**
 * De acties in `VoorraadArtikelBeheerAudit.actie` (een String-kolom): merge en
 * conversie (FO-362), vaste inhoud, en batch-69 de prijs-audit.
 */
export const VOORRAAD_BEHEER_AUDIT_ACTIE = [
  'MERGE',
  'CONVERSIE_NAAR_BULK',
  'VASTE_INHOUD',
  'PRIJS_GEWIJZIGD',
  'PRIJS_VOORGESTELD',
  'PRIJS_VOORSTEL_AFGEWEZEN',
] as const
export type VoorraadBeheerAuditActie = (typeof VOORRAAD_BEHEER_AUDIT_ACTIE)[number]

export const VOORRAAD_PRIJS_BRON = ['HANDMATIG', 'INKOOPORDER', 'IMPORT'] as const
export type VoorraadPrijsBron = (typeof VOORRAAD_PRIJS_BRON)[number]

export const VOORRAAD_PRIJS_BRON_LABELS: Record<VoorraadPrijsBron, string> = {
  HANDMATIG: 'Handmatig',
  INKOOPORDER: 'Inkooporder',
  IMPORT: 'Import',
}

export interface VoorraadPrijsVoorstelDTO {
  /** Voorgestelde prijs per eenheid (excl. btw). */
  prijs: number
  /** Huidige artikelprijs (null = nog geen prijs). */
  huidigePrijs: number | null
  inkoopOrderId: string | null
  poNummer: string | null
  op: string
}

/** Beslissing op een prijsvoorstel (één klik; afwijzen laat de prijs staan). */
export const prijsVoorstelBeslissingSchema = z.object({
  beslissing: z.enum(['ACCEPTEREN', 'AFWIJZEN']),
})
export type PrijsVoorstelBeslissingInput = z.infer<typeof prijsVoorstelBeslissingSchema>

// ── Vaste inhoud (2026-10) ────────────────────────────────────────────────
// Een gewoon (bulk) artikel dat altijd als één geheel wordt uitgegeven maar
// zelf een vaste inhoud heeft, bv. "PBM koffer gevuld" (helm, bril, oorkappen,
// LOTO-set, verbandtrommel). De inhoud is informatief: bij uitgifte gaat alleen
// het set-artikel van de voorraad af, de onderdelen niet. Opslag in
// ModuleInhoudRegel (dezelfde tabel als de lay-in-module-inhoud, FO-310).

export interface VasteInhoudRegelDTO {
  id: string
  /** Naam van het gekoppelde artikel (live), anders de vastgelegde omschrijving. */
  omschrijving: string
  aantal: number
  artikelId: string | null
  artikelNummer: string | null
}

export const VasteInhoudRegelInputSchema = z.object({
  artikelId: idParam('artikelId'),
  aantal: z.number().int().min(1, 'Minimaal 1').max(999, 'Maximaal 999'),
})

/** PUT /voorraad/artikelen/:id/vaste-inhoud — vervangt de volledige inhoud. */
export const ZetVasteInhoudSchema = z.object({
  regels: z
    .array(VasteInhoudRegelInputSchema)
    .max(100, 'Maximaal 100 regels')
    .refine((r) => new Set(r.map((x) => x.artikelId)).size === r.length, {
      message: 'Elk artikel mag maar één keer in de inhoud staan; verhoog anders het aantal.',
    }),
})
export type ZetVasteInhoudInput = z.infer<typeof ZetVasteInhoudSchema>

/** Eén leesbare regel voor lijsten: "1x Helm, 1x Bril, 2x Oordoppen". */
export function vasteInhoudTekst(regels: readonly Pick<VasteInhoudRegelDTO, 'aantal' | 'omschrijving'>[]): string {
  return regels.map((r) => `${r.aantal}x ${r.omschrijving}`).join(', ')
}

export interface VoorraadLocatieDTO {
  id: string
  naam: string
  type: string
  adres: string | null
  actief: boolean
  aantalArtikelen: number     // distinct artikelen met saldo > 0
  totaleWaarde: number        // SUM(saldo * inkoopPrijs)
}

// ─── Magazijn-indeling (2026-09) — leesmodel per stelling → vak-cel ─────────
// GET /voorraad/locaties/:id/indeling. Voedt de "Indeling"-weergave op de
// locatie-detailpagina: per stelling de vak-cellen (FO-151 stelling/plank/vak)
// met de bulk-artikelen en unieke items die er liggen, plus wat er op de
// locatie ligt zonder vaklocatie ("niet ingedeeld").

export interface IndelingArtikelDTO {
  artikelId: string
  naam: string
  artikelNummer: string
  eenheid: VoorraadEenheid
  maat: string | null
  kleur: string | null
  /** Saldo op DEZE locatie (kan 0 zijn: vak gelabeld vóór de eerste inkoop). */
  aantal: number
  gereserveerd: number
  /**
   * Totaal over ALLE locaties, zelfde grootheid als de laag-signalering:
   * bulk = SUM(saldi.aantal); traceerbaar = aantal OP_VOORRAAD-exemplaren
   * (spiegelt mapArtikelListRow in de artikellijst).
   */
  totaalAantal: number
  /** Signaleringsdrempel over alle locaties; 0 = geen voorraadbeleid. */
  minimumVoorraad: number
}

export interface IndelingUniekItemDTO {
  id: string
  artikelId: string
  artikelNaam: string
  /** Leesbare code: assetTag / serienummer / IMEI / telefoonnummer. */
  identificatie: string | null
  /**
   * Bewust ongefilterd: ook IN_REPARATIE/DEFECT/VERMIST blijft in zijn vak
   * staan zolang de locatie/vaklocatie gevuld is — de UI toont de status.
   */
  status: VoorraadUniekStatus
}

export interface IndelingVakDTO {
  plank: number | null
  vak: number | null
  /** Afgeleide code via formatVaklocatie, bv. "4-2-1", "4-2" of "4". */
  code: string
  artikelen: IndelingArtikelDTO[]
  uniekeItems: IndelingUniekItemDTO[]
}

export interface IndelingStellingDTO {
  stelling: number
  /** Cellen op looproute (compareVaklocatie): "4" vóór "4-0" vóór "4-1". */
  vakken: IndelingVakDTO[]
  /** Aantal bulk-artikel-regels in deze stelling. */
  totaalArtikelen: number
  totaalUniek: number
}

export interface VoorraadIndelingDTO {
  locatie: { id: string; naam: string }
  /** Numeriek oplopend op stellingnummer. */
  stellingen: IndelingStellingDTO[]
  /** Op deze locatie aanwezig (aantal/reservering ≠ 0 of uniek item) zonder vaklocatie. */
  nietIngedeeld: {
    artikelen: IndelingArtikelDTO[]
    uniekeItems: IndelingUniekItemDTO[]
  }
}

/**
 * Magazijnlocatie-filter (2026-10) — één keuze in de Stelling/Vak-filters op
 * /voorraad. Drie niveaus: de hele stelling, de hele plank (alle vakken op die
 * plank) en één vak. Het aantal is het aantal artikelen dat de lijst bij die
 * keuze toont (distinct; saldo-rijen én traceerbare exemplaren op voorraad).
 */
export interface VoorraadVaklocatieOptieDTO {
  niveau: 'STELLING' | 'PLANK' | 'VAK'
  stelling: number
  plank: number | null
  vak: number | null
  /** Afgeleide code via formatVaklocatie, bv. "3", "3-4" of "7-2-5". */
  code: string
  /** Aantal artikelen dat de lijst bij deze keuze toont. */
  aantalArtikelen: number
}

/** "Zonder vaklocatie" per locatie (kar, werkplaats, kluis, of het magazijn zelf). */
export interface VoorraadZonderVaklocatieLocatieDTO {
  locatieId: string
  naam: string
  aantalArtikelen: number
}

/**
 * GET /voorraad/vaklocaties — de keuzes voor de magazijnlocatie-filters,
 * server-side geteld met dezelfde regels als de artikellijst.
 */
export interface VoorraadVaklocatieOptiesDTO {
  /** Op looproute: stelling, dan de plank, dan de vakken op die plank. */
  vaklocaties: VoorraadVaklocatieOptieDTO[]
  /** Artikelen met voorraad (of een exemplaar) zonder stelling. */
  zonderVaklocatie: {
    aantalArtikelen: number
    perLocatie: VoorraadZonderVaklocatieLocatieDTO[]
  }
}

export interface VoorraadStatsDTO {
  totaalArtikelen: number
  totaalStuks: number
  totaleWaarde: number
  laagInVoorraad: number       // artikelen onder minimum
  locaties: number
}

/**
 * Onder-minimum alert (bulk-voorraad). soort = 'ONDER_MINIMUM'.
 * Link: /voorraad/<artikelId>.
 */
export interface VoorraadAlertDTO {
  soort: 'ONDER_MINIMUM'
  artikelId: string
  artikelNummer: string
  naam: string
  categorie: VoorraadCategorie
  huidigSaldo: number
  minimumVoorraad: number
  tekort: number               // minimum - huidig
  bestelSuggestie: number | null
  /** batch-69 (FO-521) — aanvulniveau/plafond; null = niet ingesteld. */
  maximumVoorraad: number | null
  /** Niveau waar `tekortTotMaximum` naartoe rekent (maximum, anders minimum). */
  aanvullenTot: number
  /** Stuks tot aan `aanvullenTot` — wat er besteld moet worden. */
  tekortTotMaximum: number
}

/**
 * Alert voor een individueel uniek item (Spec 30 Fase 8B).
 *
 * `soort`-discriminant:
 *   KEURING_VERLOPEN      — volgendeKeuring verlopen
 *   KEURING_BINNENKORT    — volgendeKeuring binnen de waarschuwingsdrempel
 *   PBM_VERVANG_NADERT    — houdbaarheidsdatum/vervangdatum nadert
 *   NIET_RETOUR           — UITGEGEVEN item voorbij verwachteRetour
 *   SIM_ZONDER_KOPPELING  — SIMKAART UITGEGEVEN zonder employeeId of telefoon
 *   VERMIST               — item met status VERMIST
 *
 * Link: /voorraad/uniek/<uniekItemId>.
 */
export type VoorraadUniekAlertSoort =
  | 'KEURING_VERLOPEN'
  | 'KEURING_BINNENKORT'
  | 'PBM_VERVANG_NADERT'
  | 'NIET_RETOUR'
  | 'SIM_ZONDER_KOPPELING'
  | 'VERMIST'

export interface VoorraadUniekAlertDTO {
  soort: VoorraadUniekAlertSoort
  uniekItemId: string
  artikelNaam: string
  categorie: VoorraadCategorie
  interneCode: string | null
  /** Naam van de huidige houder (employeeId → naam). */
  houderNaam: string | null
  /**
   * Datum die de alert triggert (ISO-string), afhankelijk van soort:
   *   KEURING_*       → volgendeKeuring
   *   PBM_VERVANG     → vervangdatum ?? houdbaarheidsdatum
   *   NIET_RETOUR     → verwachteRetour
   *   anderen         → null
   */
  signaaldatum: string | null
  /** Aantal dagen tot (negatief = in het verleden). Null als n.v.t. */
  dagenTot: number | null
}

/** Gecombineerde alert-union — alle soorten via één endpoint. */
export type VoorraadCombinedAlertDTO = VoorraadAlertDTO | VoorraadUniekAlertDTO

/**
 * Service-input voor `boekMutatie()`. `aantal` is ALTIJD positief — richting
 * wordt door de service uit `type` afgeleid via `isInkomend()`. Voor
 * VERPLAATSING geef je alleen één call (de service maakt VERPLAATSING_UIT op
 * `locatieId` én VERPLAATSING_IN op `tegenLocatieId` in dezelfde transactie).
 */
export interface BoekMutatieInput {
  artikelId: string
  locatieId: string
  type: VoorraadMutatieType
  aantal: number
  employeeId?: string
  werkbonId?: string
  inkoopOrderId?: string
  tegenLocatieId?: string
  // `string | null`: de boekMutatieSchema accepteert een expliciete null (de
  // scanner stuurt `notitie ?? null` bij een note-loze boeking) en de Prisma-
  // kolom VoorraadMutatie.notitie is `String?`, dus null is een geldige waarde.
  notitie?: string | null
  actorUserId: string
  /**
   * batch-41 FO-362 — alleen voor de traceerbaar→bulk-conversie: die boekt
   * administratieve UITGIFTE-/RETOUR-mutaties die een bestaande exemplaar-
   * registratie herregistreren, geen echt uitgifte-event. De FO-335-
   * kostenboeking moet dan overgeslagen worden (de bevroren kostvelden van
   * het exemplaar worden 1:1 op de mutatie gekopieerd — geen nieuwe kosten,
   * geen dubbeltelling). Service-only veld: zit bewust NIET in
   * `boekMutatieSchema`, dus niet via de API te zetten.
   */
  skipKostenBoeking?: boolean
  /**
   * batch-41 FO-362 — sla de VOORRAAD_LAAG-drempellogica over voor deze boeking.
   *
   * Bedoeld voor administratieve mutatie-PAREN die netto op de beginstand
   * eindigen (de traceerbaar→bulk-conversie, de kist-inname). Zet de vlag op
   * BEIDE benen: hij onderdrukt niet alleen het vuren bij een tussendip, maar
   * ook het SLUITEN van een openstaand item bij een tussenpiek. Alleen het
   * dip-been onderdrukken laat het andere been een terecht signaal wegvinken
   * dat daarna nooit terugkomt (de rising-edge vereist eerst een echte stijging).
   *
   * Service-only veld: zit bewust NIET in `boekMutatieSchema`, dus niet via de
   * API te zetten.
   */
  skipVoorraadLaagEvent?: boolean
  /**
   * batch-67 middelen-inname (FO-499 §A1) - bron van de ledgerregel die een
   * UITGIFTE aan / RETOUR van een medewerker schrijft (default HANDMATIG).
   * Service-only veld: zit bewust NIET in `boekMutatieSchema`.
   */
  ledgerBron?: MiddelMutatieBron | null
  /** Sla de ledgerregel over (alleen administratieve herregistraties); default = `skipKostenBoeking`. */
  skipLedger?: boolean
  /** Staat, foto's en formulier-sessie voor de ledgerregel van een RETOUR via het innameformulier. */
  ledgerConditie?: 'GOED' | 'GEBRUIKSSPOREN' | 'BESCHADIGD' | 'ONVOLLEDIG' | null
  ledgerFotoStorageKeys?: readonly string[]
  ledgerSessieId?: string | null
  /**
   * Kist-inname (2026-07) — kale GereedschapsDrager-id wanneer de boeking een
   * kar/koffer betreft (bulk naar/uit kist). Structurele referentie i.p.v.
   * notitie-parsing. Service-only veld: zit bewust NIET in `boekMutatieSchema`.
   */
  dragerId?: string | null
  /**
   * batch-69 magazijn-cijfers (FO-520 §C12) — de correctie komt uit deze
   * telsessie en regel. Service-only (alleen de doorvoer van een telling zet ze);
   * zit bewust NIET in `boekMutatieSchema` of `correctieSchema`.
   */
  tellingId?: string | null
  tellingRegelId?: string | null
}

// ─── Zod schemas ────────────────────────────────────────────────────────────

// batch-69 (FO-521): de velden ZONDER `.default()`. Create voegt de defaults
// toe (eenheid STUK, traceerbaar/bestelbaar false); de PATCH is
// `partialZonderDefaults` van déze basis, zodat een PATCH zonder
// `traceerbaar`/`bestelbaar` die vlaggen nooit stil op false zet (zod 4 past een
// `.default()` ook binnen `.partial()` toe; zie `zod-patch.ts`).
const artikelVeldenSchema = z.object({
  naam: z.string().trim().min(2).max(200),
  categorie: z.enum(VOORRAAD_CATEGORIE),
  eenheid: z.enum(VOORRAAD_EENHEID),
  // .nullable(): de GET-read-DTO geeft deze nullable kolommen als `null` terug;
  // een edit-form dat het artikel terugkaatst mag daar geen "Ongeldige invoer"
  // op krijgen (contract-test-harness: voorraad-artikel round-trip).
  omschrijving: z.string().max(2000).optional().nullable(),
  maat: z.string().max(50).optional().nullable(),
  kleur: z.string().max(50).optional().nullable(),
  leverancier: z.string().max(200).optional().nullable(),
  // FO-059 — gestructureerde leverancier (Client type=LEVERANCIER) voor
  // order-groepering; vrije-tekst `leverancier` blijft voor legacy/zonder-match.
  leverancierId: z.string().min(1).optional().nullable(),
  leverancierArtNr: z.string().max(100).optional().nullable(),
  inkoopPrijs: z.number().nonnegative().optional().nullable(),
  verkoopPrijs: z.number().nonnegative().optional().nullable(),
  // `.optional()` en NIET `.default(0)`: een default hoort bij create, niet in
  // de gedeelde basis (een PATCH die dit veld niet meestuurt mag het
  // voorraadbeleid nooit stil op "geen signalering" zetten). Het create-pad vult
  // de 0 zelf aan.
  minimumVoorraad: z.number().int().nonnegative().optional(),
  // batch-69 (FO-521 §B6) — aanvulniveau/plafond (voorheen "gewenste voorraad").
  // `.nullable()` omdat de GET-read-DTO `null` teruggeeft zodra er niets is
  // ingesteld; een edit-form dat het artikel terugkaatst mag daar geen
  // "Ongeldige invoer" op krijgen (contract-test-harness). `maximum >= minimum`
  // en "minimum > 0 vraagt een maximum" staan in `controleerArtikelNiveaus`.
  maximumVoorraad: z.number().int().nonnegative().max(100000).optional().nullable(),
  bestelDrempel: z.number().int().nonnegative().optional().nullable(),
  bestelAantal: z.number().int().positive().optional().nullable(),
  // Twee vormen (zie de DTO-docblock): absolute http(s)-URL óf een
  // artikelfoto-storage-key (gezet door POST /voorraad/artikelen/:id/foto).
  // Geklemd via fotoBronSchema — nooit een vrij tekstveld (blob-read-gat).
  fotoUrl: fotoBronSchema.optional().nullable(),
  actief: z.boolean().optional(),
  /** Spec 30: individueel traceerbaar (uniek) vs bulk (saldo). Server genereert qrToken. */
  traceerbaar: z.boolean(),
  /** Beschikbaar voor bestelling via bestelmodule (keuze 1 spec 30). */
  bestelbaar: z.boolean(),
  /** Interne creditkosten per eenheid (keuze 1 spec 30). */
  creditKosten: z.number().nonnegative().optional().nullable(),
  /** batch-69 (FO-521 §C10) — dit artikel nooit automatisch bestellen. */
  autoBestellenUitgesloten: z.boolean().optional(),
})

/**
 * batch-69 magazijn-cijfers (FO-521 §B6/§6) — de niveau-regels van een artikel,
 * op de (samengevoegde) eindstand. Eén functie voor het gedeelde schema, de
 * artikel-routes, het niveaus-scherm (inline, bulk, Excel) en het formulier:
 *   - maximum >= minimum (ook als CHECK-constraint in de database);
 *   - een artikel met een minimum boven 0 heeft een maximum nodig
 *     (aanvulniveau; anders weet het besteladvies niet tot waar het aanvult);
 *   - een bestelbaar bulk-artikel heeft minimum EN maximum (FO-512-regel);
 *     traceerbare artikelen vallen daarbuiten (geen voorraadbeleid in de formulieren).
 */
export interface ArtikelNiveausStaat {
  minimumVoorraad: number
  maximumVoorraad: number | null
  bestelbaar: boolean
  traceerbaar: boolean
}

export interface ArtikelNiveauFout {
  veld: 'minimumVoorraad' | 'maximumVoorraad'
  melding: string
}

export function controleerArtikelNiveaus(s: ArtikelNiveausStaat): ArtikelNiveauFout[] {
  const fouten: ArtikelNiveauFout[] = []
  if (s.maximumVoorraad != null && s.maximumVoorraad < s.minimumVoorraad) {
    fouten.push({
      veld: 'maximumVoorraad',
      melding: `Het maximum mag niet onder het minimum (${s.minimumVoorraad}) liggen`,
    })
  }
  // Het bestelbaar-vereiste geldt voor bulk: een traceerbaar artikel heeft in de
  // formulieren geen voorraadbeleid. Een ingesteld minimum vraagt wel altijd een
  // maximum (ook traceerbaar: het besteladvies vult tot het maximum aan).
  const bestelbaarBulk = s.bestelbaar && !s.traceerbaar
  if (bestelbaarBulk && s.minimumVoorraad <= 0) {
    fouten.push({
      veld: 'minimumVoorraad',
      melding: 'Een bestelbaar artikel heeft een minimum nodig (het bestelpunt)',
    })
  }
  if ((s.minimumVoorraad > 0 || bestelbaarBulk) && s.maximumVoorraad == null) {
    fouten.push({
      veld: 'maximumVoorraad',
      melding: bestelbaarBulk
        ? 'Een bestelbaar artikel heeft een maximum nodig (het aanvulniveau)'
        : 'Vul een maximum in: tot dit niveau vult het besteladvies aan',
    })
  }
  return fouten
}

/**
 * Voorstel voor een maximum als er (nog) geen verbruik bekend is: minimum plus
 * één besteleenheid; zonder besteleenheid telt het minimum als eenheid, dus
 * tweemaal het minimum. Dezelfde regel als de migratie `b69_voorraad_maximum` en
 * het aanmaken vanuit inkoop (één waarheid). Met verbruik rekent het
 * niveaus-scherm met `stelMaximumVoor` (@streep/utils).
 */
export function standaardMaximumVoorstel(minimumVoorraad: number, bestelAantal: number | null | undefined): number {
  if (minimumVoorraad <= 0) return 0
  const eenheid = bestelAantal != null && bestelAantal > 0 ? bestelAantal : minimumVoorraad
  return minimumVoorraad + eenheid
}

export const createArtikelSchema = artikelVeldenSchema
  .extend({
    eenheid: z.enum(VOORRAAD_EENHEID).default('STUK'),
    traceerbaar: z.boolean().default(false),
    bestelbaar: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    for (const f of controleerArtikelNiveaus({
      minimumVoorraad: v.minimumVoorraad ?? 0,
      maximumVoorraad: v.maximumVoorraad ?? null,
      bestelbaar: v.bestelbaar,
      traceerbaar: v.traceerbaar,
    })) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [f.veld], message: f.melding })
    }
  })
export type CreateArtikelInput = z.infer<typeof createArtikelSchema>

/**
 * PATCH: `partialZonderDefaults` (#336) op de velden-basis, zodat een weggelaten
 * veld nooit een create-default terugschrijft. Daarbij alleen de onderlinge
 * controle die zonder de huidige stand kan (maximum >= minimum als beide
 * meekomen). De route toetst de samengevoegde eindstand met
 * `controleerArtikelNiveaus` zodra een niveau- of bestelbaar-veld meekomt.
 */
export const updateArtikelSchema = partialZonderDefaults(artikelVeldenSchema).superRefine((v, ctx) => {
  if (
    v.minimumVoorraad !== undefined &&
    v.maximumVoorraad !== undefined &&
    v.maximumVoorraad !== null &&
    v.maximumVoorraad < v.minimumVoorraad
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maximumVoorraad'],
      message: `Het maximum mag niet onder het minimum (${v.minimumVoorraad}) liggen`,
    })
  }
})
export type UpdateArtikelInput = z.infer<typeof updateArtikelSchema>

export const createLocatieSchema = z.object({
  naam: z.string().trim().min(2).max(200),
  type: z.enum(['MAGAZIJN', 'SERVICEBUS', 'KANTOOR', 'EXTERN']).default('MAGAZIJN'),
  adres: z.string().max(300).optional(),
  contactNaam: z.string().max(150).optional(),
  actief: z.boolean().optional(),
})
export type CreateLocatieInput = z.infer<typeof createLocatieSchema>

export const updateLocatieSchema = partialZonderDefaults(createLocatieSchema)
export type UpdateLocatieInput = z.infer<typeof updateLocatieSchema>

/**
 * Generieke boek-payload voor de service. `actorUserId` komt server-side uit
 * de sessie, niet uit de request-body — dus niet in de Zod-schema. `type`
 * idem: dat wordt door de route-handler gezet (POST /uitgifte zet 'UITGIFTE',
 * etc.).
 */
export const boekMutatieSchema = z.object({
  // .min(1) i.p.v. .cuid(): prod-DB bevat 77-90% UUID-format IDs (legacy) die
  // .cuid() afwijst met een 400 — zie project-geheugen "Legacy UUID-format IDs".
  artikelId: z.string().min(1),
  locatieId: z.string().min(1),
  aantal: z.number().int().positive(),
  employeeId: z.string().min(1).optional(),
  werkbonId: z.string().min(1).optional(),
  inkoopOrderId: z.string().min(1).optional(),
  tegenLocatieId: z.string().min(1).optional(),
  // .nullable(): de scanner-actions (portal) sturen `notitie: input.notitie ?? null`
  // wanneer een magazijnier zonder notitie boekt. `notitie` is legitiem optioneel,
  // dus null mag — anders zou een note-loze boeking 400'en (null-rejection).
  notitie: z.string().max(500).optional().nullable(),
})
export type BoekMutatieBody = z.infer<typeof boekMutatieSchema>

/** Verplaatsing vereist een tegen-locatie. Aparte schema zodat de validator
 * dat al afdwingt voordat de route-handler de service aanroept. */
export const verplaatsingSchema = boekMutatieSchema.extend({
  tegenLocatieId: z.string().min(1),
})
export type VerplaatsingInput = z.infer<typeof verplaatsingSchema>

/**
 * batch-62 uitrusting-inkoop (FO-467 §10) — ontvangst registreren (INKOOP) met
 * de optie om openstaande telverschillen "mee te nemen": die worden in dezelfde
 * transactie alsnog afgeboekt, voor zover de nieuwe stand het toelaat.
 */
export const inkoopMutatieSchema = boekMutatieSchema.extend({
  afwikkelVerschilIds: z.array(z.string().min(1)).max(100).optional(),
})
export type InkoopMutatieInput = z.infer<typeof inkoopMutatieSchema>

/**
 * Handmatige correctie kan PLUS of MIN zijn — het Zod-schema dwingt de
 * richting expliciet af zodat de route-handler weet welk `VoorraadMutatieType`
 * te boeken.
 */
export const correctieSchema = boekMutatieSchema.extend({
  richting: z.enum(['PLUS', 'MIN']),
  // batch-62 uitrusting-inkoop (FO-467 §10) — openstaande telverschillen die
  // deze telling verklaart ("meenemen in de correctie").
  afwikkelVerschilIds: z.array(z.string().min(1)).max(100).optional(),
})
export type CorrectieInput = z.infer<typeof correctieSchema>

// ─── FO-116-B (batch-22) — voorraadmutatie boeken vanuit de medewerker-app ────
//
// De monteur mag in de app ALLEEN een retour of telling-correctie boeken op een
// bestaande locatie/artikel; nooit INKOOP/UITGIFTE/VERPLAATSING/WERKBON. Deze
// whitelist is een harde server-side grens (zowel hier in het schema als nog
// eens expliciet in de route-handler). `aantal` is altijd positief; de richting
// volgt uit het type via `isInkomend()`. `actorUserId`/`employeeId` komen
// server-side uit de sessie (nooit uit de body), dus niet in dit schema.
export const PORTAL_VOORRAAD_MUTATIE_TYPES = [
  'RETOUR',
  'CORRECTIE_PLUS',
  'CORRECTIE_MIN',
] as const
export type PortalVoorraadMutatieType = (typeof PORTAL_VOORRAAD_MUTATIE_TYPES)[number]

export const PORTAL_VOORRAAD_MUTATIE_LABELS: Record<PortalVoorraadMutatieType, string> = {
  RETOUR: 'Retour (terugbrengen)',
  CORRECTIE_PLUS: 'Correctie omhoog (+)',
  CORRECTIE_MIN: 'Correctie omlaag (-)',
}

export const portalBoekMutatieSchema = z.object({
  artikelId: z.string().min(1),
  locatieId: z.string().min(1),
  aantal: z.number().int().positive(),
  type: z.enum(PORTAL_VOORRAAD_MUTATIE_TYPES),
  notitie: z.string().trim().max(500).optional().nullable(),
})
export type PortalBoekMutatieInput = z.infer<typeof portalBoekMutatieSchema>

/** Minimale, prijs-loze artikel-optie voor de portal-picker (geen inkoopprijs). */
export interface PortalVoorraadArtikelDTO {
  id: string
  artikelNummer: string
  naam: string
  eenheid: VoorraadEenheid
}

/** Minimale locatie-optie voor de portal-picker. */
export interface PortalVoorraadLocatieDTO {
  id: string
  naam: string
  type: string
}

export const listArtikelenQuerySchema = z.object({
  search: z.string().trim().max(100).optional(),
  categorie: z.enum(VOORRAAD_CATEGORIE).optional(),
  /**
   * FO-153 — filter op hoofdgroep (afgeleide soort-as): vertaalt server-side
   * naar `categorie IN categorieenVoorHoofdgroep(...)`. Een expliciete
   * `categorie` wint (fijner filter binnen de groep).
   */
  hoofdgroep: z.enum(VOORRAAD_HOOFDGROEP).optional(),
  /** FO-151 — alleen artikelen met een saldo-rij in deze stelling. */
  stelling: z.coerce.number().int().min(1).max(999).optional(),
  /**
   * Magazijnlocatie-filter (2026-10) — verfijning binnen de stelling: plank en
   * vak matchen op DEZELFDE saldo-rij als `stelling`. Zelfde hiërarchie als de
   * opslag: vak vereist plank, plank vereist stelling.
   */
  plank: z.coerce.number().int().min(0).max(999).optional(),
  vak: z.coerce.number().int().min(0).max(999).optional(),
  /**
   * Magazijnlocatie-filter — "Zonder vaklocatie": artikelen met een saldo-rij
   * (of een exemplaar op voorraad) zonder stelling, bv. op een kar of in de
   * werkplaats. Combineert met `locatieId`, niet met `stelling`.
   */
  zonderVaklocatie: queryBoolean().optional(),
  /**
   * FO-151 — sorteer op vaklocatie (looproute: stelling → plank → vak, leeg
   * achteraan). Zonder deze param blijft de bestaande naam-sortering.
   */
  sort: z.enum(['naam', 'vaklocatie']).optional(),
  locatieId: idParam('locatieId').optional(),
  laagInVoorraad: queryBoolean().optional(), // true = alleen artikelen onder minimum
  // batch-69 (FO-521 §A4) — doorklik "waarvan zonder prijs": voorraad zonder inkoopprijs.
  zonderPrijs: queryBoolean().optional(),
  actief: queryBoolean().optional(),
  /**
   * Spec 30 filter: true = alleen traceerbaar (uniek), false = alleen bulk.
   * Strikte parse i.p.v. z.coerce.boolean(): die coercet de querystring
   * "false" naar true (elke niet-lege string is truthy), waardoor een
   * "alleen bulk"-filter stilletjes zou omklappen.
   */
  traceerbaar: z.preprocess(
    (v) => (v === true || v === 'true' ? true : v === false || v === 'false' ? false : undefined),
    z.boolean().optional(),
  ),
  /** QR-flow: alleen artikelen aangemaakt op/na deze datum (yyyy-mm-dd). */
  sindsDatum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
}).superRefine(vaklocatieFilterRefine)
export type ListArtikelenQuery = z.infer<typeof listArtikelenQuerySchema>

export const listMutatiesQuerySchema = z.object({
  artikelId: z.string().min(1).optional(),
  locatieId: z.string().min(1).optional(),
  employeeId: z.string().min(1).optional(),
  werkbonId: z.string().min(1).optional(),
  type: z.enum(VOORRAAD_MUTATIE_TYPE).optional(),
  vanaf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  totEnMet: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  search: z.string().trim().max(100).optional(),
  // batch-69 magazijn-cijfers (FO-520 §C15) — correcties uit één telling, of
  // alleen correcties die uit een telling komen.
  tellingId: z.string().min(1).max(64).optional(),
  uitTelling: queryBoolean().optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(50),
})
export type ListMutatiesQuery = z.infer<typeof listMutatiesQuerySchema>

/**
 * Query-schema voor GET /voorraad/uitgiftes — module-brede open uitgiftes.
 */
export const listUitgiftesQuerySchema = z.object({
  employeeId: z.string().min(1).optional(),
  categorie: z.enum(VOORRAAD_CATEGORIE).optional(),
  /** ISO-datumstring YYYY-MM-DD: uitgifte ná deze datum. */
  vanaf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** ISO-datumstring YYYY-MM-DD: uitgifte vóór+op deze datum. */
  totEnMet: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** Wanneer true: retourneer alleen uitgiftes ouder dan N dagen. */
  teLangUit: z.coerce.number().int().nonnegative().optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(50),
})
export type ListUitgiftesQuery = z.infer<typeof listUitgiftesQuerySchema>

// ─── Bulk-innemen vanuit het uitgiftes-overzicht ─────────────────────────────

/**
 * Eén in te nemen regel; discriminated op kind (uniek item vs bulk-retour).
 * Bestemming per regel: precies één van `locatieId` (terug op voorraad) of
 * `dragerId` (kist-inname: in een kar/koffer). `ladeId` alleen bij `dragerId`;
 * de XOR wordt op request-niveau afgedwongen (discriminatedUnion-leden moeten
 * kale objects blijven).
 */
export const innemenBulkItemSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('UNIEK'),
    uniekItemId: z.string().min(1),
    /** Locatie waar het item wordt teruggeplaatst (XOR dragerId). */
    locatieId: z.string().min(1).optional().nullable(),
    dragerId: z.string().min(1).optional().nullable(),
    ladeId: z.string().min(1).optional().nullable(),
  }),
  z.object({
    kind: z.literal('BULK'),
    artikelId: z.string().min(1),
    employeeId: z.string().min(1),
    /** Aantal dat retour komt (doorgaans het volledige open aantal). */
    aantal: z.number().int().positive(),
    locatieId: z.string().min(1).optional().nullable(),
    dragerId: z.string().min(1).optional().nullable(),
    ladeId: z.string().min(1).optional().nullable(),
    /**
     * Alleen bij dragerId: bekende herkomst-locatie van de uitgifte-rij als
     * administratieve boekingslocatie-fallback wanneer de kist zelf niet op
     * een locatie ligt (netto-effect blijft nul). Staat bewust buiten de XOR.
     */
    bekendeLocatieId: z.string().min(1).optional().nullable(),
  }),
])
export type InnemenBulkItem = z.infer<typeof innemenBulkItemSchema>

/** Request-body van POST /voorraad/uitgiftes/innemen-bulk. */
export const innemenBulkSchema = z
  .object({
    // Max 100 = de pageSize-cap van listUitgiftesQuerySchema hierboven, zodat
    // "alles op deze pagina selecteren" altijd in één aanroep past. Gaat die
    // cap ooit omhoog, verhoog deze dan mee.
    items: z.array(innemenBulkItemSchema).min(1).max(100),
  })
  .superRefine((val, ctx) => {
    val.items.forEach((item, i) => {
      const heeftLocatie = item.locatieId != null
      const heeftDrager = item.dragerId != null
      if (heeftLocatie === heeftDrager) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', i, heeftLocatie ? 'dragerId' : 'locatieId'],
          message: 'Kies per regel een locatie óf een kar/koffer als bestemming',
        })
      }
      if (item.ladeId != null && !heeftDrager) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['items', i, 'ladeId'],
          message: 'Een lade hoort bij een kar/koffer-bestemming',
        })
      }
    })
  })
export type InnemenBulkRequest = z.infer<typeof innemenBulkSchema>

/** Response van POST /voorraad/uitgiftes/innemen-bulk (best-effort per regel). */
export interface InnemenBulkResultDTO {
  /** Aantal succesvol verwerkte regels. */
  verwerkt: number
  /** Per mislukte regel: index in de request-array + leesbare fout. */
  fouten: Array<{ index: number; error: string }>
}

// ─── Rapportages DTO (Spec 30 Deel 4.7) ──────────────────────────────────────

/** Totale voorraadwaarde (bulk + uniek). */
export interface VoorraadWaardeDTO {
  /** SUM(saldo.aantal * artikel.inkoopPrijs) over alle bulk-saldi. */
  bulk: number
  /** SUM(aanschafBedrag) voor niet-AFGESCHREVEN, niet-verwijderde unieke items. */
  uniek: number
  /** Totaal (bulk + uniek). */
  totaal: number
}

/** Telt unieke items per status VERMIST / DEFECT. */
export interface VermistDefectDTO {
  vermist: number
  defect: number
}

/** Materiaalverbruik (WERKBON-mutaties) per kalendermaand. */
export interface MaterialenPerMaandDTO {
  /** ISO-jaar. */
  jaar: number
  /** Maandnummer 1-12. */
  maand: number
  /** Goed-leesbaar label, bijv. "jan 2025". */
  maandLabel: string
  /** Totaal aantal eenheden verbruikt. */
  aantalEenheden: number
  /** Totale waarde in euro's (aantal * inkoopPrijs; null als prijs niet bekend). */
  waarde: number | null
}

/** Kledingverbruik (UITGIFTE-mutaties WERKKLEDING/PBM) per maat. */
export interface KledingPerMaatDTO {
  /** Maat als string, bijv. "43", "XL"; null = niet ingevuld op artikel. */
  maat: string | null
  /** Aantal uitgegeven eenheden. */
  aantalEenheden: number
}

/** Medewerker met het meeste uitgegeven unieke items. */
export interface UitgiftePerMedewerkerDTO {
  employeeId: string
  naam: string
  aantalUniekeItems: number
}

/**
 * Volledig rapportage-aggregate (GET /voorraad/rapportages).
 * Alle geld-waarden zijn reeds omgezet naar `number` (van Prisma Decimal).
 * Datums zijn Europe/Amsterdam.
 */
export interface VoorraadRapportagesDTO {
  voorraadwaarde: VoorraadWaardeDTO
  /**
   * batch-69 (FO-521 §A4) — "waarvan zonder prijs": bulk-artikelen met voorraad
   * zonder inkoopprijs en exemplaren zonder aanschafbedrag, over dezelfde set als
   * `voorraadwaarde` (die tellen dus niet mee in de waarde).
   */
  zonderPrijs: { artikelen: number; exemplaren: number }
  /** Aantal bulk-artikelen waarvan het totaal-saldo <= minimumVoorraad. */
  onderMinimumAantal: number
  /** Totaal aantal unieke items met status UITGEGEVEN. */
  uitgegevenUniekeItems: number
  vermistDefect: VermistDefectDTO
  uitgegevenPerMedewerker: UitgiftePerMedewerkerDTO[]
  materiaalverbruikPerPeriode: MaterialenPerMaandDTO[]
  kledingverbruikPerMaat: KledingPerMaatDTO[]
}

// ─── batch-41 voorraad (FO-362) — duplicaat-merge + traceerbaar→bulk-conversie ─

/** Eén artikel binnen een duplicaat-groep (keuze-informatie voor de merge). */
export interface DuplicaatArtikelDTO {
  id: string
  artikelNummer: string
  naam: string
  maat: string | null
  kleur: string | null
  categorie: VoorraadCategorie
  eenheid: VoorraadEenheid
  traceerbaar: boolean
  actief: boolean
  inkoopPrijs: number | null
  minimumVoorraad: number
  leverancierArtNr: string | null
  /** Som van alle saldo-rijen (bulk). */
  totaalSaldo: number
  /** Niet-verwijderde exemplaren (traceerbaar). */
  aantalExemplaren: number
  aantalMutaties: number
  createdAt: string
}

/** Vermoedelijke duplicaat-groep — suggestie, nooit auto-merge. */
export interface DuplicaatGroepDTO {
  /** Stabiele groepssleutel (gesorteerde artikel-ids, gejoined). */
  groepKey: string
  /** Waarop de groep matcht. */
  matchTypes: Array<'NAAM_MAAT_KLEUR' | 'LEVERANCIER_ARTNR'>
  artikelen: DuplicaatArtikelDTO[]
}

export const mergeArtikelenSchema = z.object({
  /** De artikelen die opgaan in het doel-artikel (1..10 per merge). */
  bronArtikelIds: z.array(z.string().min(1)).min(1).max(10),
})
export type MergeArtikelenInput = z.infer<typeof mergeArtikelenSchema>

/** Saldo-regel in de merge-preview (per artikel per locatie). */
export interface MergePreviewSaldoDTO {
  locatieId: string
  locatieNaam: string
  aantal: number
  gereserveerd: number
  vaklocatie: string | null
}

/** Per bron-artikel: wat er omgehangen/opgeteld gaat worden. */
export interface MergePreviewBronDTO {
  artikel: DuplicaatArtikelDTO
  saldi: MergePreviewSaldoDTO[]
  aantalOpenBestellingen: number
  aantalActieveReserveringen: number
  /** Wijkt de inkoopprijs af van het doel-artikel? (§7.4: geen stille herwaardering) */
  prijsWijktAf: boolean
}

export interface MergePreviewDTO {
  doel: DuplicaatArtikelDTO
  doelSaldi: MergePreviewSaldoDTO[]
  bronnen: MergePreviewBronDTO[]
  /** Saldo per locatie ná de merge (doel + alle bronnen). */
  gecombineerdeSaldi: MergePreviewSaldoDTO[]
  totaalNaMerge: number
  /** Drempel die na de merge geldt (van het overlevende artikel, §8). */
  minimumVoorraadNaMerge: number
  /** Blokkerend: bulk en traceerbaar door elkaar (eerst converteren). */
  traceerbaarMix: boolean
  waarschuwingen: string[]
}

export interface MergeResultDTO {
  auditId: string
  doelArtikelId: string
  samengevoegd: number
  nieuweAliassen: number
}

/** Reden waarom een exemplaar de conversie blokkeert (§7.3/T6). */
export interface ConversieBlokkerendExemplaarDTO {
  id: string
  serienummer: string | null
  assetTag: string | null
  status: string
  reden: string
}

export interface ConversiePreviewDTO {
  artikelId: string
  artikelNaam: string
  traceerbaar: boolean
  /** OP_VOORRAAD-exemplaren die saldo worden, gegroepeerd per locatie. */
  opVoorraadPerLocatie: Array<{
    locatieId: string
    locatieNaam: string
    aantal: number
  }>
  /** UITGEGEVEN-exemplaren die een bulk-uitgifte-regel worden. */
  uitgegeven: Array<{
    id: string
    employeeId: string
    employeeNaam: string
    serienummer: string | null
  }>
  /** AFGESCHREVEN (niet-verwijderde) exemplaren die blijven staan (historie). */
  aantalAfgeschrevenBlijft: number
  blokkerend: ConversieBlokkerendExemplaarDTO[]
}

export interface ConversieResultDTO {
  auditId: string
  artikelId: string
  saldoIngeboekt: number
  uitgiftesBehouden: number
  nieuweAliassen: number
}

/**
 * Scanner-resolve: saldo-informatie per locatie voor de uitgifte-actie
 * (FO-362 §7.6/T8 — default = de locatie met vaklocatie-match, want het
 * stelling-etiket is locatie-gebonden).
 */
export interface ScannerLocatieSaldoDTO {
  locatieId: string
  naam: string
  aantal: number
  heeftVaklocatie: boolean
}

/** Lichte medewerker-optie voor de scanner-uitgifte (geen gevoelige velden). */
export interface ScannerEmployeeOptionDTO {
  id: string
  naam: string
}
